/**
 * Per-site official school calendar PDF paths (mirror parentPacketHandbook.js).
 * Prefer token-scoped API PDF when the site has a calendar form slot.
 * 14th-ave: official 63rd-St/Brooklyn calendar at /packet/14th-ave/…
 */

export const CALENDAR_ICS_BUTTON_LABEL = 'Add school dates to your calendar';

export const CALENDAR_FORM_BY_SITE = {
  rockland: 'form_05',
  '55th-street': 'bs_16',
  woodmere: 'wm_16',
  '14th-ave': 'fa_02',
};

const STATIC_CALENDAR_BY_SITE = {
  '55th-street': '/packet/55th-street/school-calendar-2026-2027.pdf',
  '14th-ave': '/packet/14th-ave/school-calendar-2026-2027.pdf',
  woodmere: '/packet/woodmere/woodmere-school-calendar-2026-2027-es.pdf',
};

const ROCKLAND_DATE_LIST_PATH = '/packet/rockland/rockland-date-list-calendar-2026-2027.pdf';

/** Inline PNG twin of the Rockland date-list calendar. Shown as an image in the
 * packet page so phones/in-app browsers (which can't render PDFs) still see the
 * schedule; the PDF stays available as a download link. */
export const ROCKLAND_DATE_LIST_IMAGE_PATH = '/packet/rockland/rockland-date-list-calendar-2026-2027.png';

export function calendarFormIdForSite(siteSlug) {
  return CALENDAR_FORM_BY_SITE[siteSlug] || null;
}

export function allCalendarFormIds() {
  return Object.values(CALENDAR_FORM_BY_SITE);
}

/**
 * Anchor props for PDF/ICS buttons — real link navigation in a new tab.
 * Do NOT use window.open() for these: it leaves a blank tab in Android
 * WebView / in-app email browsers and some Safari versions (2026-07-24
 * Rockland blank-calendar reports; the PDFs themselves were healthy).
 * Spread onto a MUI Button: <Button {...externalPdfLinkProps(href)}>.
 */
export function externalPdfLinkProps(href) {
  return {
    component: 'a',
    href,
    target: '_blank',
    rel: 'noopener noreferrer',
  };
}

/** Resolve href for parent-facing generated calendar (API PDF). */
export function parentCalendarPdfHref({ siteSlug, token, apiBaseUrl }) {
  const calendarFormId = calendarFormIdForSite(siteSlug);
  if (calendarFormId && token && apiBaseUrl) {
    return `${apiBaseUrl}/packet/${encodeURIComponent(token)}/${encodeURIComponent(calendarFormId)}/pdf/`;
  }
  return STATIC_CALENDAR_BY_SITE[siteSlug] || null;
}

/** Devorah Excel date-list Rockland calendar (static PDF). */
export function parentRocklandDateListCalendarPdfHref({ siteSlug }) {
  if (siteSlug !== 'rockland') return null;
  return ROCKLAND_DATE_LIST_PATH;
}

/** DB-driven school-year calendar (.ics) for the student's session. */
export function parentCalendarIcsHref({ token, apiBaseUrl }) {
  if (!token || !apiBaseUrl) return null;
  return `${apiBaseUrl}/packet/${encodeURIComponent(token)}/calendar.ics`;
}

export function isCalendarPacketForm(formId, siteSlug) {
  return formId === calendarFormIdForSite(siteSlug);
}

/** Woodmere summer program static .ics (wm_16 copy references this). */
export const WOODMERE_SUMMER_ICS_PATH = '/packet/woodmere/woodmere-summer-2026.ics';

/** Woodmere summer program calendar PDF (opens in browser; pairs with school-year wm_16 PDF). */
export const WOODMERE_SUMMER_PDF_PATH = '/packet/woodmere/woodmere-summer-calendar-2026.pdf';

export function parentWoodmereSummerPdfHref({ siteSlug }) {
  if (siteSlug !== 'woodmere') return null;
  return WOODMERE_SUMMER_PDF_PATH;
}

